import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import FormModal from '../components/FormModal';
import { ActionsSkeletonCell } from '../components/ActionsSkeletonCell';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Search } from 'lucide-react';
import { marketRegularizationReasonsAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { Skeleton } from '../components/Skeleton';
import { usePageLine } from '../hooks/usePageLine';

interface RegularizationReason {
  id: number;
  reason_text: string;
  display_order: number;
  is_active: boolean;
  project_id: number | null;
  created_at?: string;
}

export default function MarketRegularizationReasonsPage() {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  // The sentence under the title, in the catalogue's words.
  const pageLine = usePageLine();

  const [reasons, setReasons] = useState<RegularizationReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingReason, setEditingReason] = useState<RegularizationReason | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<RegularizationReason | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [formState, setFormState] = useState({
    reasonText: '',
    displayOrder: '',
    isActive: true,
  });

  const fetchReasons = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await marketRegularizationReasonsAPI.getAll(selectedProjectId!);
      if (response.data.status === 'success') {
        setReasons(response.data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch reasons:', err);
      setError(err.response?.data?.message || 'Failed to fetch reasons');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  const resetForm = () => {
    setFormState({ reasonText: '', displayOrder: '', isActive: true });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (!formState.reasonText.trim()) {
      setFormError('Reason text is required.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        reason_text: formState.reasonText.trim(),
        display_order: formState.displayOrder ? Number(formState.displayOrder) : 0,
        is_active: formState.isActive,
      };

      if (modalMode === 'create') {
        await marketRegularizationReasonsAPI.create({ ...payload, project_id: selectedProjectId });
      } else if (editingReason) {
        await marketRegularizationReasonsAPI.update(String(editingReason.id), payload);
      }

      setIsModalOpen(false);
      resetForm();
      setFormError(null);
      fetchReasons();
    } catch (err: any) {
      console.error('Failed to save reason:', err);
      setFormError(err.response?.data?.message || 'Failed to save reason');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (reason: RegularizationReason) => {
    try {
      setIsSubmitting(true);
      await marketRegularizationReasonsAPI.delete(String(reason.id));
      setDeleteConfirm(null);
      fetchReasons();
    } catch (err: any) {
      console.error('Failed to delete reason:', err);
      setError(err.response?.data?.message || 'Failed to delete reason');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateModal = () => {
    resetForm();
    setFormError(null);
    setModalMode('create');
    setEditingReason(null);
    setIsModalOpen(true);
  };

  const openEditModal = (reason: RegularizationReason) => {
    setModalMode('edit');
    setEditingReason(reason);
    setFormError(null);
    setFormState({
      reasonText: reason.reason_text,
      displayOrder: String(reason.display_order || 0),
      isActive: reason.is_active,
    });
    setIsModalOpen(true);
  };

  const filteredReasons = useMemo(() => {
    if (!searchQuery) return reasons;
    const q = searchQuery.toLowerCase();
    return reasons.filter((r) => r.reason_text.toLowerCase().includes(q));
  }, [reasons, searchQuery]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Market Regularization Reasons</h1>
          {pageLine && <p className="mt-1 truncate text-sm text-gray-500" title={pageLine}>{pageLine}</p>}
        </div>
        <button
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
          onClick={openCreateModal}
        >
          Add New Reason
        </button>
      </div>

      {/* Table (search + table in one container) */}
      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by reason text..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th className="whitespace-nowrap px-6 py-4">Reason Text</th>
                <th className="whitespace-nowrap px-6 py-4">Display Order</th>
                <th className="whitespace-nowrap px-6 py-4">Status</th>
                <th className="whitespace-nowrap px-6 py-4">Created Date</th>
                <th className="sticky right-0 z-10 whitespace-nowrap bg-gray-100 px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white text-left">
              {loading ? (
                <>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-40" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-12" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-5 w-16 rounded-full" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                      <ActionsSkeletonCell />
                    </tr>
                  ))}
                </>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <p className="text-sm text-red-500">{error}</p>
                    <button
                      onClick={fetchReasons}
                      className="mt-2 text-sm text-blue-600 hover:underline"
                    >
                      Try again
                    </button>
                  </td>
                </tr>
              ) : filteredReasons.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500">
                      {reasons.length === 0
                        ? 'No reasons found. Click "Add New Reason" to create one.'
                        : 'No reasons match your search.'}
                    </p>
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="mt-2 text-sm text-blue-600 hover:underline"
                      >
                        Clear search
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredReasons.map((reason) => (
                  <tr key={reason.id} className="group hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{reason.reason_text}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{reason.display_order}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        reason.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {reason.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-600">
                      {reason.created_at
                        ? new Date(reason.created_at).toLocaleDateString('en-IN')
                        : '-'}
                    </td>
                    <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-6 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                      <TableActionGroup>
                        <TableActionButton action="edit" label={`Edit ${reason.reason_text}`} type="button" onClick={() => openEditModal(reason)} />
                        <TableActionButton action="delete" label={`Delete ${reason.reason_text}`} type="button" onClick={() => setDeleteConfirm(reason)} />
                      </TableActionGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <FormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={modalMode === 'create' ? 'Add New Reason' : 'Edit Reason'}
        size="md"
        formId="reason-form"
        isSubmitting={isSubmitting}
        submitLabel={modalMode === 'create' ? 'Add Reason' : 'Save Changes'}
      >
        <form id="reason-form" className="space-y-6" onSubmit={handleSubmit}>
          {formError && <FormErrorBanner message={formError} />}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="reason-text">
              Reason Text
            </label>
            <input
              id="reason-text"
              type="text"
              value={formState.reasonText}
              onChange={(e) => setFormState((prev) => ({ ...prev, reasonText: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="e.g. Store Closed, Market Visit"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="display-order">
              Display Order
            </label>
            <input
              id="display-order"
              type="number"
              min="0"
              value={formState.displayOrder}
              onChange={(e) => setFormState((prev) => ({ ...prev, displayOrder: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="0"
            />
            <p className="text-xs text-gray-500">Lower numbers appear first in the mobile app.</p>
          </div>

          {modalMode === 'edit' && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700" htmlFor="is-active">
                Active
              </label>
              <button
                id="is-active"
                type="button"
                role="switch"
                aria-checked={formState.isActive}
                onClick={() => setFormState((prev) => ({ ...prev, isActive: !prev.isActive }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formState.isActive ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formState.isActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-500">
                {formState.isActive ? 'Visible in mobile app' : 'Hidden from mobile app'}
              </span>
            </div>
          )}
        </form>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        isSubmitting={isSubmitting}
        title="Delete Reason"
        entityLabel="reason"
        entityName={deleteConfirm?.reason_text}
      />
    </div>
  );
}
